const FIXED_REF = /[?&]ref=([A-Za-z0-9]{16,32})(?:&|#|$)/;

// O QR só identifica o código. A presença continua dependendo da confirmação
// explícita, do GPS e da validação do servidor.
export function parseScannedQr(raw) {
  const value = String(raw ?? '').trim();
  const fixed = value.match(FIXED_REF);
  if (fixed) return { kind: 'fixed', ref: fixed[1] };
  if (/^pn1\.\S+$/.test(value)) return { kind: 'challenge', value };
  return { kind: 'invalid' };
}

export const CAMERA_CONSTRAINTS = [
  { audio: false, video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } } },
  { audio: false, video: true },
];

export function cameraConstraints(deviceId) {
  if (!deviceId) return CAMERA_CONSTRAINTS;
  return [{ audio: false, video: { deviceId: { exact: deviceId } } }, ...CAMERA_CONSTRAINTS];
}

export async function listCameras(mediaDevices) {
  if (!mediaDevices?.enumerateDevices) return [];
  try {
    const devices = await mediaDevices.enumerateDevices();
    return devices
      .filter((device) => device.kind === 'videoinput' && device.deviceId)
      .map((device, index) => ({ id: device.deviceId, label: device.label || `Câmera ${index + 1}` }));
  } catch {
    return [];
  }
}

export function activeDeviceId(stream) {
  const [track] = stream?.getVideoTracks?.() || [];
  return track?.getSettings?.().deviceId || '';
}

const RETRYABLE = new Set(['OverconstrainedError', 'ConstraintNotSatisfiedError', 'NotFoundError']);

// Tenta a câmera traseira e cai para qualquer câmera quando ela não existe.
export async function openCameraStream(mediaDevices, constraints = CAMERA_CONSTRAINTS) {
  let lastError = null;
  for (const constraint of constraints) {
    try {
      return await mediaDevices.getUserMedia(constraint);
    } catch (error) {
      lastError = error;
      if (!RETRYABLE.has(error?.name)) throw error;
    }
  }
  throw lastError || new Error('Câmera indisponível.');
}

export function cameraFailure(error, { secureContext = true, supported = true } = {}) {
  if (!secureContext) {
    return { state: 'insecure', message: 'Para usar a câmera, abra o PontoNow em uma conexão HTTPS.' };
  }
  if (!supported) {
    return {
      state: 'unsupported',
      message: 'Este navegador não permite usar a câmera. Abra o link no Chrome ou no Safari atualizado, ou digite o PIN.',
    };
  }
  const name = error?.name;
  if (name === 'NotAllowedError' || name === 'SecurityError' || name === 'PermissionDeniedError') {
    return {
      state: 'denied',
      message: 'Permissão da câmera negada. Libere a câmera nas configurações do navegador ou digite o PIN.',
    };
  }
  if (RETRYABLE.has(name) || name === 'DevicesNotFoundError') {
    return { state: 'unavailable', message: 'Nenhuma câmera disponível neste aparelho. Digite o PIN.' };
  }
  if (name === 'NotReadableError' || name === 'TrackStartError' || name === 'AbortError') {
    return {
      state: 'unavailable',
      message: 'A câmera está em uso por outro aplicativo. Feche-o e tente de novo, ou digite o PIN.',
    };
  }
  return { state: 'error', message: 'Não foi possível abrir a câmera. Tente de novo ou digite o PIN.' };
}

export const VIDEO_FAILURE = {
  state: 'video',
  message: 'A câmera abriu, mas a imagem não pôde ser exibida. Toque em "Tentar novamente" ou digite o PIN.',
};

export const DECODER_FAILURE = {
  state: 'unsupported',
  message: 'Este navegador não consegue ler QR pela câmera. Digite o PIN.',
};

export function stopStream(stream) {
  stream?.getTracks?.().forEach((track) => track.stop());
}
